import React, { useEffect } from 'react';
import TrackingJS from '@vp/react-tracking';
import { currentCulture } from 'src/utils/cultureUtil';
import { isProdTenant } from 'src/utils/tenantUtil';

export interface AnalyticsPageData {
  pageName: string;
  pageSection: string;
  pageStage: string;
  culture?: string;
}

export interface AnalyticsEventData {
  action: string;
  label: string;
  pageStage: string;
  pageName: string;
  variant?: unknown;
  productId?: string;
  name?: string;
  eventDetail?: string;
  category?: string;
  pageSection?: string;
  core_product_id?: string;
  core_product_version?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Analytics = ({ culture }: { culture?: string }): any => {
  return (
    <TrackingJS
      tenant="vistaprint" //until our pages are in promotique, we are hard-coding this to vistaprint
      culture={culture || currentCulture}
      environment={isProdTenant() ? 'prod' : 'dev'}
    />
  );
};

/**
 * Valid Design Services page tracking events is documented here:
 * https://vistaprint.atlassian.net/wiki/spaces/DST/pages/1330544777/Design+Services+Page+Tracking
 */
export function usePageTracking(analyticsPageData: AnalyticsPageData): void {
  useEffect(() => sendPageTracking(analyticsPageData), [analyticsPageData]);
}

/**
 * Valid Design Services page tracking events is documented here:
 * https://vistaprint.atlassian.net/wiki/spaces/DST/pages/1330544777/Design+Services+Page+Tracking
 */
export function sendPageTracking(analyticsPageData: AnalyticsPageData): void {
  if (window?.tracking?.page) {
    window.tracking.page({
      ...analyticsPageData,
      genTrack: '',
      languageLocale: analyticsPageData.culture || currentCulture,
    });
  } else {
    setTimeout(() => sendPageTracking(analyticsPageData), 1000);
  }
}

export function sendShopperTracking(shopperId: string): void {
  if (window?.tracking?.identify) {
    window.tracking.identify(shopperId);
  } else {
    setTimeout(() => sendShopperTracking(shopperId), 1000);
  }
}

/**
 * Valid Design Services click tracking events are documented here:
 * https://vistaprint.atlassian.net/wiki/spaces/DST/pages/1332740898/Design+Services+Event+Click+Tracking
 */
export function sendEventTracking(analyticsEventData: AnalyticsEventData): void {
  const { action, ...trackingData } = analyticsEventData;

  if (window?.tracking?.track) {
    window.tracking.track(action, {
      category: 'Design Services',
      pageSection: 'Design Services',
      ...trackingData,
    });
  } else {
    setTimeout(() => sendEventTracking(analyticsEventData), 1000);
  }
}
